'use client';

import { useCallback, useMemo, useReducer } from 'react';
import * as React from 'react';
import { noop } from 'lodash';
import { createContext } from 'use-context-selector';
import { EMPTY_OBJECT } from '@frameio/utils';
import { suppressedFlushSync } from '@frameio/vapor-migration/utils/suppressedFlushSync';
import type { AppState, AppStateActions } from './AppState';
import { appStateReducer, DEFAULT_STATE } from './AppState';

type AppStateContextValue = AppState & {
  dispatch: React.Dispatch<AppStateActions>;
};

const DEFAULT_VALUE: AppStateContextValue = {
  ...DEFAULT_STATE,
  dispatch: noop,
};

export const AppStateContext =
  createContext<AppStateContextValue>(DEFAULT_VALUE);
AppStateContext.displayName = 'AppStateContext';

export const AppStateContextProvider: React.FC<
  React.PropsWithChildren<unknown> & { defaultValues?: AppState }
> = React.memo(({ children, defaultValues = EMPTY_OBJECT }) => {
  const memoizedDefaultValue = useMemo(
    () => ({ ...DEFAULT_VALUE, ...defaultValues }),
    [defaultValues]
  );
  const [state, dispatchAsync] = useReducer(
    appStateReducer,
    memoizedDefaultValue
  );

  const dispatch = useCallback((...args: Parameters<typeof dispatchAsync>) => {
    suppressedFlushSync(() => {
      dispatchAsync(...args);
    });
  }, []);
  const value = useMemo(() => ({ ...state, dispatch }), [state, dispatch]);

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
});
